import { useState } from 'react';
import { Flag } from 'lucide-react';
import type { StudentSummary } from '@/lib/api/trainer';
import { StudentDetailSheet } from './StudentDetailSheet';

function formatLastActive(dt: string | null): string {
  if (!dt) return 'Never';
  const diffDays = Math.floor((Date.now() - new Date(dt).getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}

function statusBadge(pct: number): { cls: string; label: string } {
  if (pct >= 70) return { cls: 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-400', label: 'On Track' };
  if (pct >= 30) return { cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/60 dark:text-yellow-400', label: 'At Risk' };
  return { cls: 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-400', label: 'Behind' };
}

interface Props {
  students: StudentSummary[];
  batchId: string;
}

export function StudentsTab({ students, batchId }: Props) {
  const [selected, setSelected] = useState<StudentSummary | null>(null);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  if (students.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-[#6b7280]">
        No students enrolled in this batch.
      </div>
    );
  }

  const hasFlagged = students.some((s) => s.is_flagged);
  const visible = showFlaggedOnly ? students.filter((s) => s.is_flagged) : students;

  return (
    <>
      <div className="mx-auto max-w-7xl p-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm text-[#6b7280]">
            {students.length} student{students.length !== 1 ? 's' : ''} enrolled
          </span>
          {hasFlagged && (
            <button
              onClick={() => setShowFlaggedOnly((v) => !v)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                showFlaggedOnly
                  ? 'border-orange-300 bg-orange-50 text-orange-700'
                  : 'border-[#ececec] text-[#6b7280] hover:text-[#111827]'
              }`}
            >
              <Flag className="h-3 w-3" />
              Follow-up
            </button>
          )}
        </div>
        <div className="overflow-hidden rounded-lg border border-[#ececec]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#ececec] bg-[#f3f4f6]/40">
                <th className="px-4 py-3 text-left font-medium text-[#6b7280]">Student</th>
                <th className="px-4 py-3 text-left font-medium text-[#6b7280]">Progress</th>
                <th className="px-4 py-3 text-left font-medium text-[#6b7280]">Attendance</th>
                <th className="px-4 py-3 text-left font-medium text-[#6b7280]">Last Active</th>
                <th className="px-4 py-3 text-left font-medium text-[#6b7280]">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const { cls, label } = statusBadge(s.progress_pct);
                return (
                  <tr
                    key={s.id}
                    onClick={() => setSelected(s)}
                    className="cursor-pointer border-b border-[#ececec] last:border-0 transition-colors hover:bg-[#fafafa]/30"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#f3f4f6] text-xs font-semibold overflow-hidden">
                          {s.avatar_url ? (
                            <img src={s.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                          ) : (
                            s.username[0].toUpperCase()
                          )}
                        </div>
                        <span className="font-medium">{s.username}</span>
                        {s.is_flagged && (
                          <Flag className="h-3 w-3 fill-orange-500 text-orange-500 flex-shrink-0" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-[#f3f4f6]">
                          <div
                            className="h-full rounded-full bg-[#ff5a1f] transition-all"
                            style={{ width: `${Math.min(100, Math.round(s.progress_pct))}%` }}
                          />
                        </div>
                        <span className="text-xs text-[#6b7280]">
                          {s.completed_topics}/{s.total_topics}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{Math.round(s.attendance_pct)}%</td>
                    <td className="px-4 py-3 text-sm text-[#6b7280]">
                      {formatLastActive(s.last_active)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
                        {label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <StudentDetailSheet
          student={selected}
          batchId={batchId}
          open={!!selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
